import React, { useState, useMemo } from "react";
import { X, Download, Loader2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  getAbvCategory,
  getIbuCategory,
  getGroupBadgeColor as getBeerCategoryColor,
  getLevelBadgeColor as getBeerLevelColor,
} from "../../utils/beerTaxonomy";

export default function BeerReferenceSheet({
  isOpen,
  onClose,
  items = [],
  restaurantName = "All Restaurants",
  selectedItems = [],
  onFetchAllItems,
}) {
  const [isExporting, setIsExporting] = useState(false);

  const itemsToShow = useMemo(() => {
    let baseItems = items;
    if (selectedItems && selectedItems.length > 0) {
      baseItems = items.filter((i) => selectedItems.includes(i.uuid || i.id));
    }

    return [...baseItems].sort((a, b) => {
      // 1. Style Group / Category
      const catA = (a.category || "").toLowerCase();
      const catB = (b.category || "").toLowerCase();
      if (catA !== catB) return catA.localeCompare(catB);

      // 2. Core Style (sub_category or style.name or style)
      const styleA = (a.sub_category || a.style?.name || a.style || "").toLowerCase();
      const styleB = (b.sub_category || b.style?.name || b.style || "").toLowerCase();
      if (styleA !== styleB) return styleA.localeCompare(styleB);

      // 3. Beer Name
      const nameA = (a.product_name || a.name || "").toLowerCase();
      const nameB = (b.product_name || b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [items, selectedItems]);

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      let itemsToExport = items;

      if (onFetchAllItems) {
        try {
          const fetched = await onFetchAllItems();
          if (fetched && Array.isArray(fetched) && fetched.length > 0) {
            itemsToExport = fetched;
          } else if (fetched && fetched.data && Array.isArray(fetched.data)) {
            itemsToExport = fetched.data;
          }
        } catch (err) {
          console.error("Error fetching all items:", err);
        }
      }

      const sortedItems = [...itemsToExport].sort((a, b) => {
        const catA = (a.category || "").toLowerCase();
        const catB = (b.category || "").toLowerCase();
        if (catA !== catB) return catA.localeCompare(catB);

        const styleA = (a.sub_category || a.style?.name || a.style || "").toLowerCase();
        const styleB = (b.sub_category || b.style?.name || b.style || "").toLowerCase();
        if (styleA !== styleB) return styleA.localeCompare(styleB);

        const nameA = (a.product_name || a.name || "").toLowerCase();
        const nameB = (b.product_name || b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const allData = sortedItems.map((item, index) => {
        const regionStr =
          [
            item.origin?.country || item.region?.country,
            item.origin?.state || item.region?.state,
            item.origin?.city || item.region?.city,
          ]
            .filter(Boolean)
            .join(" / ") || item.region || "-";

        const abvCat = item.abv_category || getAbvCategory(item.abv);
        const ibuCat = item.ibu_category || getIbuCategory(item.ibu);

        const abvIbu = `ABV: ${item.abv || "0"}% ${abvCat ? `(${abvCat})` : ""}\nIBU: ${item.ibu || "0"} ${ibuCat ? `(${ibuCat})` : ""}`;

        const sensory = [
          item.body ? `Body: ${item.body}` : null,
          item.texture ? `Texture: ${item.texture}` : null,
          item.intensity ? `Flavor Intensity: ${item.intensity}` : null,
        ]
          .filter(Boolean)
          .join("\n") || "-";

        const attributes = [
          item.is_non_alcoholic ? "Non-Alc" : null,
          item.is_vegan || item.vegan === "Yes" ? "Vegan" : null,
          item.has_lactose ? "Lactose" : null,
          item.has_nuts ? "Nuts" : null,
          item.has_gluten === false ? "Gluten-Free" : null,
          item.is_filtered || item.filtered === "Yes" ? "Filtered" : null,
        ]
          .filter(Boolean)
          .join(", ") || "-";

        const prices = [];
        if (item.formats?.find((f) => f.format === "draft")) {
          const draft = item.formats.find((f) => f.format === "draft");
          prices.push(`Draft: $${draft.prices?.[0] || "0"} (${draft.serving_sizes?.[0] || ""})`);
        }
        if (item.formats?.find((f) => f.format === "can")) {
          const can = item.formats.find((f) => f.format === "can");
          prices.push(`Can: $${can.prices?.[0] || "0"} (${can.serving_sizes?.[0] || ""})`);
        }
        if (item.formats?.find((f) => f.format === "bottle")) {
          const btl = item.formats.find((f) => f.format === "bottle");
          prices.push(`Btl: $${btl.prices?.[0] || "0"} (${btl.serving_sizes?.[0] || ""})`);
        }

        return [
          index + 1,
          item.brewery_name || "-",
          item.product_name || item.name || "Unknown",
          item.category || "-",
          item.style?.name || item.style || item.sub_category || "-",
          regionStr,
          abvIbu,
          sensory,
          attributes,
          prices.join("\n") || "-",
        ];
      });

      autoTable(doc, {
        head: [
          [
            "ID",
            "Brewery",
            "Beer Name",
            "Style Group",
            "Core Style",
            "Origin",
            "Strength & Bitterness",
            "Profile",
            "Attributes",
            "Format & Price",
          ],
        ],
        body: allData,
        theme: "striped",
        headStyles: { fillColor: [136, 19, 55] }, // #881337
        styles: { fontSize: 7, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 8 },
          1: { cellWidth: 26 },
          2: { cellWidth: 28 },
          3: { cellWidth: 24 },
          4: { cellWidth: 24 },
          5: { cellWidth: 28 },
          6: { cellWidth: 28 },
          7: { cellWidth: 34 },
          8: { cellWidth: 32 },
          9: { cellWidth: 32 },
        },
        startY: 20,
        margin: { top: 25 },
        didDrawPage: function (data) {
          doc.setFontSize(16);
          doc.setTextColor(136, 19, 55);
          doc.text("Beer Reference Sheet", 14, 15);
          doc.setFontSize(10);
          doc.setTextColor(100);
        },
      });

      doc.save(`beer-reference-sheet.pdf`);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const content = (
    <div
      className={`bg-[#F9F9F9] w-full ${
        isOpen ? "max-w-[1400px] h-[90vh] shadow-xl rounded-lg" : "h-full"
      } overflow-hidden flex flex-col relative`}
    >
      {isOpen && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-sm hover:bg-gray-50"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      )}

      <div className={`px-4 pt-4 pb-2 bg-[#F9F9F9] ${!isOpen && "border-b border-gray-100"}`}>
        <div className="flex justify-between items-start mb-3">
          <div>
            <h1 className="text-lg text-start font-bold text-gray-900 line-clamp-1">
              Beer Reference Sheet
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Full sensory profiles, characteristics, and pricing
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Speak Your Menu</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="flex items-center gap-2 px-3 py-2 bg-[#881337] text-white rounded-md shadow-sm hover:bg-[#881337]/90 transition-colors text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {isExporting ? "Exporting..." : "Export PDF"}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 pb-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-left border-collapse table-fixed">
            <thead className="bg-[#FFF9F5] border-b border-[#FDE6D2] sticky top-0 z-10">
              <tr>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-24">BREWERY</th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-28">BEER NAME</th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-24">STYLE GROUP</th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-24">CORE STYLE</th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-28">ORIGIN</th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-28">STRENGTH & BITTERNESS</th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-36">PROFILE</th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-28">ATTRIBUTES</th>
                <th className="py-2 px-2 text-right text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-28">FORMAT & PRICE</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {itemsToShow.map((item, index) => {
                const abvCat = item.abv_category || getAbvCategory(item.abv);
                const ibuCat = item.ibu_category || getIbuCategory(item.ibu);
                const regionStr = [
                  item.origin?.country || item.region?.country,
                  item.origin?.state || item.region?.state,
                  item.origin?.city || item.region?.city,
                ].filter(Boolean).join(" / ") || item.region || "-";

                return (
                  <tr key={item.uuid || index} className="hover:bg-gray-50/50 transition-colors">
                    {/* BREWERY */}
                    <td className="py-2 px-2 text-[9px] font-bold text-gray-900 align-top line-clamp-2">
                      {item.brewery_name || "-"}
                    </td>

                    {/* BEER NAME */}
                    <td className="py-2 px-2 text-[9px] font-bold text-gray-700 align-top break-words">
                      {item.product_name || item.name || "-"}
                    </td>

                    {/* STYLE GROUP */}
                    <td className="py-2 px-2 align-top">
                      {item.category ? (
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border inline-block ${getBeerCategoryColor(item.category)}`}>
                          {item.category}
                        </span>
                      ) : (
                        <span className="text-[9px] text-gray-400">-</span>
                      )}
                    </td>

                    {/* CORE STYLE */}
                    <td className="py-2 px-2 text-[9px] align-top">
                      {item.style?.name || item.style || item.sub_category ? (
                        <span className="px-1.5 py-0.5 rounded text-[8px] font-bold border inline-block bg-pink-50 text-pink-700 border-pink-100">
                          {item.style?.name || item.style || item.sub_category}
                        </span>
                      ) : (
                        <span className="text-[9px] text-gray-400">-</span>
                      )}
                    </td>

                    {/* ORIGIN */}
                    <td className="py-2 px-2 text-[9px] text-gray-600 align-top break-words">
                      {regionStr}
                    </td>

                    {/* STRENGTH & BITTERNESS */}
                    <td className="py-2 px-2 text-[9px] align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-gray-600">ABV: {item.abv || "0"}%</span>
                          {abvCat && (
                            <span className={`px-1 py-0.2 rounded text-[8px] border font-bold capitalize ${getBeerLevelColor(abvCat)}`}>
                              {abvCat}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-gray-600">IBU: {item.ibu || "0"}</span>
                          {ibuCat && (
                            <span className={`px-1 py-0.2 rounded text-[8px] border font-bold capitalize ${getBeerLevelColor(ibuCat)}`}>
                              {ibuCat}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* PROFILE */}
                    <td className="py-2 px-2 text-[9px] align-top">
                      <div className="flex flex-col gap-0.5 text-[8px]">
                        {item.body ? (
                          <span>
                            <span className="font-bold text-gray-500">Body:</span>{" "}
                            <span className="px-1 py-0.2 bg-gray-100 text-gray-600 border border-gray-200 rounded">{item.body}</span>
                          </span>
                        ) : null}
                        {item.texture ? (
                          <span>
                            <span className="font-bold text-gray-500">Texture:</span>{" "}
                            <span className="px-1 py-0.2 bg-gray-100 text-gray-600 border border-gray-200 rounded">{item.texture}</span>
                          </span>
                        ) : null}
                        {item.intensity ? (
                          <span>
                            <span className="font-bold text-gray-500">Flavor Intensity:</span>{" "}
                            <span className="px-1 py-0.2 bg-purple-50 text-purple-600 border border-purple-100 font-bold">{item.intensity}</span>
                          </span>
                        ) : null}
                        {!(item.body || item.texture || item.intensity) && <span className="text-gray-400">-</span>}
                      </div>
                    </td>

                    {/* ATTRIBUTES */}
                    <td className="py-2 px-2 text-[9px] align-top">
                      <div className="flex flex-wrap gap-1">
                        {item.is_non_alcoholic && <span className="px-1 py-0.2 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[8px] font-bold">Non-Alc</span>}
                        {(item.is_vegan || item.vegan === "Yes") && <span className="px-1 py-0.2 bg-green-50 text-green-700 border border-green-100 rounded text-[8px] font-bold">Vegan</span>}
                        {item.has_lactose && <span className="px-1 py-0.2 bg-orange-50 text-orange-700 border border-orange-100 rounded text-[8px] font-bold">Lactose</span>}
                        {item.has_nuts && <span className="px-1 py-0.2 bg-red-50 text-red-700 border border-red-100 rounded text-[8px] font-bold">Nuts</span>}
                        {item.has_gluten === false && <span className="px-1 py-0.2 bg-green-50 text-green-700 border border-green-100 rounded text-[8px] font-bold">Gluten-Free</span>}
                        {(item.is_filtered || item.filtered === "Yes") && <span className="px-1 py-0.2 bg-gray-100 text-gray-600 border border-gray-200 rounded text-[8px] font-bold">Filtered</span>}
                        {!(item.is_non_alcoholic || item.is_vegan || item.vegan === "Yes" || item.has_lactose || item.has_nuts || item.has_gluten === false || item.is_filtered || item.filtered === "Yes") && (
                          <span className="text-gray-400 text-[8px]">-</span>
                        )}
                      </div>
                    </td>

                    {/* FORMAT & PRICE */}
                    <td className="py-2 px-2 text-right align-top">
                      <div className="flex flex-col gap-1 items-end text-[8px]">
                        {item.formats?.find((f) => f.format === "draft") && (
                          <div className="flex items-center gap-1">
                            <span className="font-bold px-1 py-0.2 bg-blue-50 text-blue-600 border border-blue-100 rounded uppercase">DRAFT</span>
                            <span className="text-gray-600">{item.formats.find((f) => f.format === "draft").serving_sizes?.join(", ") || ""}</span>
                            <span className="font-bold text-green-700">${item.formats.find((f) => f.format === "draft").prices?.[0] || "0"}</span>
                          </div>
                        )}
                        {item.formats?.find((f) => f.format === "can") && (
                          <div className="flex items-center gap-1">
                            <span className="font-bold px-1 py-0.2 bg-gray-100 text-gray-700 border border-gray-200 rounded uppercase">CAN</span>
                            <span className="text-gray-600">{item.formats.find((f) => f.format === "can").serving_sizes?.join(", ") || ""}</span>
                            <span className="font-bold text-green-700">${item.formats.find((f) => f.format === "can").prices?.[0] || "0"}</span>
                          </div>
                        )}
                        {item.formats?.find((f) => f.format === "bottle") && (
                          <div className="flex items-center gap-1">
                            <span className="font-bold px-1 py-0.2 bg-purple-50 text-purple-600 border border-purple-100 rounded uppercase">BTL</span>
                            <span className="text-gray-600">{item.formats.find((f) => f.format === "bottle").serving_sizes?.join(", ") || ""}</span>
                            <span className="font-bold text-green-700">${item.formats.find((f) => f.format === "bottle").prices?.[0] || "0"}</span>
                          </div>
                        )}
                        {!(item.formats?.length) && <span className="text-gray-400">-</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {itemsToShow.length === 0 && (
            <div className="py-12 text-center text-gray-400 text-xs">No beers available in this view.</div>
          )}
        </div>
      </div>

      <div className="px-8 py-3 bg-[#F9F9F9] border-t border-gray-200 flex justify-between items-center text-[10px] text-gray-400">
        <div>
          For internal use only • Data synced from Speak Your Menu on {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );

  if (isOpen) {
    return (
      <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
        {content}
      </div>
    );
  }

  return content;
}
